import React, { useState, useEffect } from "react";
import { userAPI, availabilityAPI } from "../../services/api"; // update this path if needed
import axiosInstance from '../../services/axiosInstance';

// Skeleton Loader Components
const AvailabilitySkeleton = () => (
  <div className="space-y-3">
    {[...Array(7)].map((_, idx) => (
      <div key={idx} className="flex flex-col sm:flex-row justify-between items-start sm:items-center border rounded-md px-4 py-3 gap-3 sm:gap-6">
        {/* Day Label Skeleton */}
        <div className="w-28">
          <div className="h-4 bg-gray-200 rounded w-20 animate-pulse"></div>
        </div>

        {/* Right Side Skeleton */}
        <div className="flex items-center gap-4 flex-wrap sm:flex-nowrap sm:justify-end w-full sm:w-auto">
          {/* Toggle Skeleton */}
          <div className="w-10 h-6 bg-gray-200 rounded-full animate-pulse"></div>

          {/* Time Selectors Skeleton */}
          <div className="flex items-center gap-2">
            <div className="w-20 h-8 bg-gray-200 rounded animate-pulse"></div>
            <div className="w-4 h-4 bg-gray-200 rounded animate-pulse"></div>
            <div className="w-20 h-8 bg-gray-200 rounded animate-pulse"></div>
          </div>
        </div>
      </div>
    ))}
  </div>
);

const SettingsSkeleton = () => (
  <div className="space-y-4">
    {/* Session Duration Skeleton */}
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <div className="w-4 h-4 bg-gray-200 rounded animate-pulse"></div>
        <div>
          <div className="h-4 bg-gray-200 rounded w-32 animate-pulse mb-1"></div>
          <div className="h-3 bg-gray-200 rounded w-48 animate-pulse"></div>
        </div>
      </div>
      <div className="w-full h-10 bg-gray-200 rounded animate-pulse"></div>
    </div>

    {/* Buffer Time Skeleton */}
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <div className="w-4 h-4 bg-gray-200 rounded animate-pulse"></div>
        <div>
          <div className="h-4 bg-gray-200 rounded w-36 animate-pulse mb-1"></div>
          <div className="h-3 bg-gray-200 rounded w-40 animate-pulse"></div>
        </div>
      </div>
      <div className="w-full h-10 bg-gray-200 rounded animate-pulse"></div>
    </div>
  </div>
);

const SaveButtonSkeleton = () => (
  <div className="flex items-center justify-between">
    <div className="h-4 bg-gray-200 rounded w-24 animate-pulse"></div>
    <div className="flex items-center gap-3">
      <div className="h-10 w-20 bg-gray-200 rounded animate-pulse"></div>
      <div className="h-10 w-32 bg-gray-200 rounded animate-pulse"></div>
    </div>
  </div>
);

const Settings = () => {
  console.log("Settings component mounted");
  // ----- User Info State -----
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [profileImage, setProfileImage] = useState("");
  const [timezone, setTimezone] = useState("");
  const [userId, setUserId] = useState("");
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState("");
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [changingPassword, setChangingPassword] = useState(false);
  const [passwordMessage, setPasswordMessage] = useState({ type: '', text: '' });
  const [imageUpdateCounter, setImageUpdateCounter] = useState(0);

  // ----- About Info State -----
  const [professionalSummary, setProfessionalSummary] = useState("");
  const [education, setEducation] = useState("");
  const [certifications, setCertifications] = useState("");
  const [areasOfExpertise, setAreasOfExpertise] = useState("");
  const [specializations, setSpecializations] = useState("");
  const [experience, setExperience] = useState("");
  const [languages, setLanguages] = useState("");
  const [gender, setGender] = useState("");

  // Set userId from localStorage only once
  useEffect(() => {
    const userString = localStorage.getItem("user");
    if (userString) {
      const user = JSON.parse(userString);
      const localUserId = user.id || user._id || "";
      setUserId(localUserId);
    }
  }, []);

  // Fetch user info when userId is set
  useEffect(() => {
    if (!userId) return;
    console.log("Fetching user info for userId:", userId);
    const fetchUserInfo = async () => {
      try {
        const response = await userAPI.getDoctorById(userId);
        const user = response.doctor;
        setName(user.name || "");
        setEmail(user.email || "");
        setProfileImage(user.profileImage || "");
        setTimezone(user.timezone || "Asia/Kolkata");
        // Populate about fields
        if (user.about) {
          setProfessionalSummary(user.about.professionalSummary || "");
          setEducation(user.about.education || "");
          setCertifications(user.about.certifications || "");
          setAreasOfExpertise(user.about.areasOfExpertise || "");
          setSpecializations(user.about.specializations || "");
          setExperience(user.about.experience || "");
          setLanguages(user.about.languages || "");
          setGender(user.about.gender || "");
        }
      } catch (error) {
        console.error("Error fetching user info from API:", error);
      }
    };
    fetchUserInfo();
  }, [userId]);

  // Helper function to get correct image URL
  const getProfileImageUrl = (imagePath) => {
    console.log('getProfileImageUrl called with:', imagePath);
    if (!imagePath) return "https://cdn-icons-png.flaticon.com/512/149/149071.png";

    // If it's already a full URL, return as is
    if (imagePath.startsWith('https')) {
      console.log('Returning full URL as is:', imagePath);
      return imagePath;
    }

    // Use the same domain as the API but without /api
    const baseUrl = 'https://backend.adhdrapidcare.com';

    // If it's a relative path, add the base URL
    if (imagePath.startsWith('/')) {
      const fullUrl = `${baseUrl}${imagePath}`;
      console.log('Constructed full URL from relative path:', fullUrl);
      return fullUrl;
    }

    // If it doesn't start with /, add it
    const fullUrl = `${baseUrl}/${imagePath}`;
    console.log('Constructed full URL from path without slash:', fullUrl);
    return fullUrl;
  };

  // Handle image selection
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedImage(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // Handle password change
  const handlePasswordChange = async () => {
    if (!passwordData.currentPassword || !passwordData.newPassword || !passwordData.confirmPassword) {
      setPasswordMessage({ type: 'error', text: 'All fields are required' });
      return;
    }

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setPasswordMessage({ type: 'error', text: 'New passwords do not match' });
      return;
    }

    if (passwordData.newPassword.length < 6) {
      setPasswordMessage({ type: 'error', text: 'New password must be at least 6 characters long' });
      return;
    }

    setChangingPassword(true);
    setPasswordMessage({ type: '', text: '' });

    try {
      const response = await axiosInstance.put(`/users/change-password`, {
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword
      });

      if (response.data) {
        setPasswordMessage({ type: 'success', text: 'Password changed successfully!' });
        setPasswordData({
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        });
        setTimeout(() => {
          setShowPasswordModal(false);
          setPasswordMessage({ type: '', text: '' });
        }, 2000);
      }
    } catch (error) {
      console.error('Error changing password:', error);
      setPasswordMessage({
        type: 'error',
        text: 'Failed to change password: ' + (error.response?.data?.message || error.message || 'Unknown error')
      });
    } finally {
      setChangingPassword(false);
    }
  };

  // ----- Availability State -----
  const days = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
  const [availability, setAvailability] = useState(
    days.map(() => ({
      enabled: false,
      from: "9:00 AM",
      to: "5:00 PM",
    }))
  );
  const [slotDuration, setSlotDuration] = useState(30);
  const [breakDuration, setBreakDuration] = useState(10);
  const [loadingAvailability, setLoadingAvailability] = useState(true);
  const [savingAvailability, setSavingAvailability] = useState(false);
  const [saveMessage, setSaveMessage] = useState({ type: '', text: '' });

  // Fetch availability data
  useEffect(() => {
    const fetchAvailability = async () => {
      if (!userId) return;

      try {
        setLoadingAvailability(true);
        console.log('Fetching availability for user:', userId);

        const response = await availabilityAPI.getAvailabilityByUserId(userId);
        console.log('Availability response:', response);

        if (response.message && response.availability) {
          const data = response.availability;

          console.log('Raw availability data from API:', data);
          console.log('Slot duration from API:', data.slotDuration);
          console.log('Break duration from API:', data.breakDuration);

          // Update timezone
          setTimezone(data.timezone || "Asia/Kolkata");

          // Update slot and break duration
          setSlotDuration(data.slotDuration || 30);
          setBreakDuration(data.breakDuration || 10);

          // Update session duration from API (convert slotDuration to sessionDuration format)
          const sessionDurationValue = data.slotDuration || 30;
          console.log('Setting session duration from API:', sessionDurationValue, 'minutes');
          setSessionDuration(`${sessionDurationValue} minutes`);

          // Update availability for each day
          const updatedAvailability = days.map(day => {
            const dayData = data.availability && data.availability[day];
            console.log(`Processing ${day}:`, dayData);

            if (dayData && dayData.start && dayData.end) {
              // Convert time format if needed
              const startTime = formatTimeForAPI(dayData.start);
              const endTime = formatTimeForAPI(dayData.end);

              console.log(`${day} - Start: ${dayData.start} -> ${startTime}, End: ${dayData.end} -> ${endTime}`);

              return {
                enabled: true,
                from: startTime,
                to: endTime,
              };
            } else {
              console.log(`${day} - No data found, using defaults`);
              return {
                enabled: false,
                from: "9:00 AM",
                to: "5:00 PM",
              };
            }
          });

          setAvailability(updatedAvailability);
          console.log('Updated availability state:', updatedAvailability);
        } else {
          console.log('No availability data found, using defaults');
          // Keep default values if no data found
        }
      } catch (error) {
        console.error("Error fetching availability:", error);
        console.error("Error details:", error.response?.data || error.message);

        // Handle specific error cases
        if (error.response?.status === 404) {
          console.log('No availability found for user, using default values');
          // This is normal for new users who haven't set availability yet
        } else {
          console.error("Unexpected error fetching availability");
        }

        // Keep default values if fetch fails
      } finally {
        setLoadingAvailability(false);
      }
    };

    const fetchSessionPreferences = async () => {
      if (!userId) return;

      try {
        console.log('Fetching toggle preferences for user:', userId);

        // Load toggle preferences from localStorage only
        const savedTogglePreferences = localStorage.getItem(`togglePreferences_${userId}`);
        if (savedTogglePreferences) {
          const preferences = JSON.parse(savedTogglePreferences);
          console.log('Loaded toggle preferences from localStorage:', preferences);
          setAutoJoin(preferences.autoJoin || false);
          setNotesPopup(preferences.notesPopup || false);
          setManualApproval(preferences.manualApproval || false);
          setEmailReminders(preferences.emailReminders !== undefined ? preferences.emailReminders : true);
          setPostSessionEmails(preferences.postSessionEmails !== undefined ? preferences.postSessionEmails : true);
        } else {
          console.log('No toggle preferences found in localStorage, using defaults');
          // Keep default values if no data found
        }
      } catch (error) {
        console.error("Error loading toggle preferences:", error);
        // Keep default values if load fails
      }
    };

    fetchAvailability();
    fetchSessionPreferences();
  }, [userId]);

  const handleToggle = (index) => {
    const updated = [...availability];
    updated[index].enabled = !updated[index].enabled;
    setAvailability(updated);
  };

  const handleTimeChange = (index, key, value) => {
    const updated = [...availability];
    updated[index][key] = value;
    setAvailability(updated);
  };

  const timeOptions = [
    "8:00 AM", "9:00 AM", "10:00 AM", "11:00 AM",
    "12:00 PM", "1:00 PM", "2:00 PM", "3:00 PM",
    "4:00 PM", "5:00 PM", "6:00 PM", "7:00 PM",
  ];

  // Helper function to format time to HH:MM AM/PM format
  const formatTimeForAPI = (timeString) => {

    if (timeString.match(/^\d{1,2}:\d{2}\s(AM|PM)$/)) {
      return timeString;
    }

    if (timeString.match(/^\d{2}:\d{2}$/)) {
      const [hours, minutes] = timeString.split(':');
      const hour = parseInt(hours);
      const ampm = hour >= 12 ? 'PM' : 'AM';
      const displayHour = hour % 12 || 12;
      return `${displayHour.toString().padStart(2, '0')}:${minutes} ${ampm}`;
    }


    if (timeString.match(/^\d{1,2}:\d{2}\s(AM|PM)$/)) {
      const [time, ampm] = timeString.split(' ');
      const [hours, minutes] = time.split(':');
      return `${hours.padStart(2, '0')}:${minutes} ${ampm}`;
    }

    return timeString;
  };


  const saveAllSettings = async () => {
    try {
      setSavingAvailability(true);
      setSaveMessage({ type: '', text: '' });

      console.log('Starting to save all settings...');
      console.log('Selected image:', selectedImage ? selectedImage.name : 'No image selected');


      if (userId) {
        try {
          let profileData;
          if (selectedImage) {
            profileData = new FormData();
            profileData.append('name', name);
            profileData.append('email', email);
            profileData.append('timezone', timezone);
            profileData.append('profileImage', selectedImage);
            profileData.append('about', JSON.stringify({
              professionalSummary,
              education,
              certifications,
              areasOfExpertise,
              specializations,
              experience,
              languages,
              gender
            }));
          } else {
            profileData = {
              name,
              email,
              timezone,
              about: {
                professionalSummary,
                education,
                certifications,
                areasOfExpertise,
                specializations,
                experience,
                languages,
                gender
              }
            };
          }
          const profileResponse = await axiosInstance.put(`/users/${userId}`, profileData, selectedImage ? {
            headers: { 'Content-Type': 'multipart/form-data' },
          } : {});

          console.log('Profile update response:', profileResponse);

          if (profileResponse.data) {
            const userString = localStorage.getItem("user");
            if (userString) {
              const user = JSON.parse(userString);
              const updatedUser = {
                ...user,
                name: name,
                email: email,
                timezone: timezone,
                profileImage: profileResponse.data.user?.profileImage || profileResponse.data.profileImage || user.profileImage
              };
              localStorage.setItem("user", JSON.stringify(updatedUser));
            }

            let newImageUrl = null;
            if (profileResponse.data.user && profileResponse.data.user.profileImage) {
              newImageUrl = profileResponse.data.user.profileImage;
            } else if (profileResponse.data.profileImage) {
              newImageUrl = profileResponse.data.profileImage;
            }

            // Update local state directly after save
            setName(profileResponse.data.user?.name || name);
            setProfileImage(newImageUrl || profileImage);
            setTimezone(profileResponse.data.user?.timezone || timezone);
            // Optionally update other fields if returned by API

            // Clear image preview and selected image
            setImagePreview("");
            setSelectedImage(null);

            // Force re-render by incrementing counter
            setImageUpdateCounter(prev => prev + 1);

            setSelectedImage(null);
            setImagePreview("");

            console.log('Profile update successful');
          }
        } catch (error) {
          console.error('Error updating profile:', error);
          setSaveMessage({
            type: 'error',
            text: 'Failed to update profile: ' + (error.response?.data?.message || error.message || 'Unknown error')
          });
          return;
        }
      }

      // Build the availability object
      const availabilityData = {};
      days.forEach((day, index) => {
        if (availability[index].enabled) {
          const startTime = formatTimeForAPI(availability[index].from);
          const endTime = formatTimeForAPI(availability[index].to);
          availabilityData[day] = {
            start: startTime,
            end: endTime
          };
        }
      });

      const availabilityPayload = {
        userId: userId,
        timezone: timezone,
        slotDuration: parseInt(sessionDuration.split(' ')[0]), // Convert "60 minutes" to 60
        breakDuration: breakDuration,
        availability: availabilityData
      };


      const togglePreferencesPayload = {
        userId: userId,
        autoJoin: autoJoin,
        notesPopup: notesPopup,
        manualApproval: manualApproval,
        emailReminders: emailReminders,
        postSessionEmails: postSessionEmails
      };

      console.log('Saving availability with payload:', availabilityPayload);
      console.log('Saving toggle preferences to localStorage:', togglePreferencesPayload);

      // Save availability, session duration, and buffer time to API
      const availabilityResponse = await availabilityAPI.createDoctorAvailability(availabilityPayload);

      // Save toggle preferences to localStorage only
      localStorage.setItem(`togglePreferences_${userId}`, JSON.stringify(togglePreferencesPayload));

      if (availabilityResponse.message) {
        setSaveMessage({ type: 'success', text: 'All settings saved successfully!' });
        setTimeout(() => setSaveMessage({ type: '', text: '' }), 3000);
      } else {
        setSaveMessage({ type: 'error', text: 'Failed to save settings: ' + (availabilityResponse.message || 'Unknown error') });
      }
    } catch (error) {
      console.error('Error saving settings:', error);
      setSaveMessage({
        type: 'error',
        text: 'Error saving settings: ' + (error.response?.data?.message || error.message || 'Unknown error')
      });
    } finally {
      setSavingAvailability(false);
    }
  };

  // Keep the old function name for backward compatibility
  const saveAvailability = saveAllSettings;

  const [sessionDuration, setSessionDuration] = useState("30 minutes");
  const [autoJoin, setAutoJoin] = useState(false);
  const [notesPopup, setNotesPopup] = useState(false);
  const [manualApproval, setManualApproval] = useState(false);
  const [emailReminders, setEmailReminders] = useState(true);
  const [postSessionEmails, setPostSessionEmails] = useState(true);
  const [bufferTime, setBufferTime] = useState("10 minutes");


  useEffect(() => {
    console.log('Session duration changed to:', sessionDuration);
  }, [sessionDuration]);

  return (
    <div className="space-y-10">
      {/* Account Info */}
      <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
        <h2 className="text-lg font-semibold text-gray-700 mb-6 flex items-center gap-2">
          <img src="/images/SVG (8).svg" alt="Account Icon" className="w-5 h-5 object-contain" />
          Account Information
        </h2>
        <div className="flex  items-center lg:w-44 flex-shrink-0 pb-6">
          <img
            src={imagePreview || (profileImage ? `${getProfileImageUrl(profileImage)}?v=${imageUpdateCounter}` : "https://cdn-icons-png.flaticon.com/512/149/149071.png")}
            alt="Profile"
            className="w-28 h-28 rounded-full object-cover border-2 border-gray-100"
            key={`${profileImage || 'default'}-${imageUpdateCounter}`}
            onError={(e) => {
              e.target.src = "https://cdn-icons-png.flaticon.com/512/149/149071.png";
            }}
          />
          <label className="cursor-pointer mt-16">
            <input type="file" className="hidden" onChange={handleImageChange} accept="image/*" />
            <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center text-white hover:bg-blue-600 transition">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
              </svg>
            </div>
          </label>
          {selectedImage && (
            <p className="text-xs text-blue-600 mt-1">Image selected</p>
          )}
        </div>
        <div className="flex flex-col-reverse lg:flex-row-reverse gap-6">
          {/* Profile Image on Right */}


          {/* Form */}
          <div className="flex-1 w-full">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-gray-600 font-medium mb-1">Full Name</label>
                <input type="text" value={name} onChange={(e) => setName(e.target.value)} className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-1 focus:ring-blue-500 focus:border-blue-500" placeholder="Enter your full name" />
              </div>

              <div>
                <label className="block text-sm text-gray-600 font-medium mb-1">Email Address</label>
                <input
                  type="email"
                  value={email || ""}
                  readOnly
                  className="w-full border border-gray-200 bg-gray-100 text-gray-500 rounded-md px-3 py-2 text-sm focus:ring-0 focus:border-gray-300 cursor-not-allowed"
                  placeholder="Enter your email address"
                />
              </div>


              <div>
                <label className="block text-sm text-gray-600 font-medium mb-1">Education</label>
                <input type="text" value={education} onChange={e => setEducation(e.target.value)} className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-1 focus:ring-blue-500" placeholder="e.g. MBBS, FRACGP, Medical University" />
              </div>

              <div>
                <label className="block text-sm text-gray-600 font-medium mb-1">Certifications</label>
                <input type="text" value={certifications} onChange={e => setCertifications(e.target.value)} className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-1 focus:ring-blue-500" placeholder="e.g. Medical License" />
              </div>

              <div>
                <label className="block text-sm text-gray-600 font-medium mb-1">Areas of Expertise</label>
                <input type="text" value={areasOfExpertise} onChange={e => setAreasOfExpertise(e.target.value)} className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-1 focus:ring-blue-500" placeholder="e.g. Preventive Care" />
              </div>

              <div>
                <label className="block text-sm text-gray-600 font-medium mb-1">Specializations</label>
                <input type="text" value={specializations} onChange={e => setSpecializations(e.target.value)} className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-1 focus:ring-blue-500" placeholder="e.g. General Practice" />
              </div>

              <div>
                <label className="block text-sm text-gray-600 font-medium mb-1">Experience</label>
                <input type="text" value={experience} onChange={e => setExperience(e.target.value)} className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-1 focus:ring-blue-500" placeholder="e.g. 10+ Years" />
              </div>

              <div>
                <label className="block text-sm text-gray-600 font-medium mb-1">Languages</label>
                <input type="text" value={languages} onChange={e => setLanguages(e.target.value)} className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-1 focus:ring-blue-500" placeholder="e.g. English, Hindi" />
              </div>

              <div>
                <label className="block text-sm text-gray-600 font-medium mb-1">Gender</label>
                <select value={gender} onChange={e => setGender(e.target.value)} className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-1 focus:ring-blue-500">
                  <option value="">Select Gender</option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              <div>
                <label className="block text-sm text-gray-600 font-medium mb-1">Timezone</label>
                <select value={timezone} onChange={(e) => setTimezone(e.target.value)} className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm bg-white focus:ring-1 focus:ring-blue-500">
                  <option value="Asia/Kolkata">India (Asia/Kolkata)</option>
                  <option value="Australia/Sydney">Australia (Sydney)</option>
                  <option value="America/New_York">New York (EST)</option>
                  <option value="Europe/London">London (GMT)</option>
                </select>
              </div>
            </div>

            {/* Full-width Row */}
            <div className="mt-4">
              <label className="block text-sm text-gray-600 font-medium mb-1">Professional Summary</label>
              <textarea value={professionalSummary} onChange={e => setProfessionalSummary(e.target.value)} rows={3} className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-1 focus:ring-blue-500" placeholder="Enter your professional summary" />
            </div>

            {/* Change Password Button */}
            {/* <div className="mt-4 text-right">
              <button onClick={() => setShowPasswordModal(true)} className="bg-white text-blue-600 font-medium px-4 py-2 rounded-md border border-blue-500 hover:bg-blue-50 transition text-sm">
                Change Password
              </button>
            </div> */}
          </div>
        </div>
      </div>


      {/* Availability Settings */}
      <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
        <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
          Availability Settings
        </h2>



        {loadingAvailability ? (
          <div className="space-y-6">
            {/* Availability Days Skeleton */}
            <AvailabilitySkeleton />

            {/* Divider */}
            <hr className="my-6" />

            {/* Settings Skeleton */}
            <SettingsSkeleton />
          </div>
        ) : (
          <>
            {days.map((day, index) => (
              <div
                key={day}
                className="flex flex-col sm:flex-row justify-between items-start sm:items-center border rounded-md px-4 py-3 mb-3 gap-3 sm:gap-6"
              >
                {/* Day Label */}
                <div className="w-28 text-sm font-medium text-gray-700">{day}</div>

                {/* Right Side: Toggle + Time Range */}
                <div className="flex items-center gap-4 flex-wrap sm:flex-nowrap sm:justify-end w-full sm:w-auto">
                  {/* Toggle */}
                  <button
                    onClick={() => handleToggle(index)}
                    className={`w-10 h-6 rounded-full relative transition-colors duration-200 ${availability[index].enabled ? "bg-blue-500" : "bg-gray-300"
                      }`}
                  >
                    <span
                      className={`absolute left-0 top-0 h-6 w-6 bg-white rounded-full shadow transform transition-transform duration-200 ${availability[index].enabled ? "translate-x-4" : ""
                        }`}
                    ></span>
                  </button>

                  {/* Time Selectors */}
                  <div className="flex items-center gap-2">
                    <select
                      value={availability[index].from}
                      onChange={(e) => handleTimeChange(index, "from", e.target.value)}
                      className="border border-gray-300 rounded-md px-1 py-0.5 bg-white text-xs focus:ring-1 focus:ring-blue-500 h-7"
                      style={{ fontSize: '11px' }}
                    >
                      {timeOptions.map((time) => (
                        <option key={time} value={time} className="text-xs py-0">
                          {time}
                        </option>
                      ))}
                    </select>
                    <span className="text-xs text-gray-500">to</span>
                    <select
                      value={availability[index].to}
                      onChange={(e) => handleTimeChange(index, "to", e.target.value)}
                      className="border border-gray-300 rounded-md bg-white px-1 py-0.5 text-xs focus:ring-1 focus:ring-blue-500 h-7"
                      style={{ fontSize: '11px' }}
                    >
                      {timeOptions.map((time) => (
                        <option key={time} value={time} className="text-xs py-0">
                          {time}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            ))}

            {/* Divider */}
            <hr className="my-6" />

            {/* Session Buffer Time */}
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <svg
                  className="w-4 h-4 text-blue-500"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <div>
                  <p className="text-sm font-medium text-gray-700">Session Buffer Time</p>
                  <p className="text-xs text-gray-500">Time between sessions</p>
                </div>
              </div>

              <select
                value={breakDuration}
                onChange={(e) => setBreakDuration(parseInt(e.target.value))}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm bg-white focus:outline-none focus:ring-1 focus:ring-blue-500"
              >
                <option value={5}>5 minutes</option>
                <option value={10}>10 minutes</option>
                <option value={15}>15 minutes</option>
                <option value={30}>30 minutes</option>
              </select>
            </div>
          </>
        )}
      </div>

      {/* Zoom + Session + Notification */}
      <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
        {/* Section Title */}
        <h2 className="text-lg font-semibold text-gray-800 mb-4">Zoom Integration</h2>

        {/* Zoom Integration Card */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between px-4 py-4">

          {/* Left: Zoom Info + Button */}
          <div>
            <div className="flex items-center gap-3 mb-2">
              <img
                src="/images/SVG (14).svg" // update with correct path
                alt="Zoom Icon"
                className="w-5 h-5 object-contain"
              />
              <div>
                <h3 className="text-sm font-semibold text-gray-800">Zoom Account</h3>
                <p className="text-sm text-gray-500">Connect your Zoom account for video sessions</p>
              </div>
            </div>

            <button className="bg-blue-600 text-white px-4 py-1.5 text-sm rounded-md hover:bg-blue-700 transition">
              Connect Zoom Account
            </button>
          </div>

          {/* Right: Status */}
          <div className="flex items-center gap-2 mb-10 ">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-4 w-4 text-blue-500"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.707a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414L9 13.414l4.707-4.707z"
                clipRule="evenodd"
              />
            </svg>
            <span className="text-sm text-blue-600 font-medium ">Not Connected</span>
          </div>
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
        {/* Header */}
        <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
          Session Preferences
        </h2>

        {/* Dropdown */}
        <div className="mb-6">
          <label className="block text-sm text-gray-700 mb-1">Default Session Duration</label>
          <div className="relative">
            <select
              value={sessionDuration}
              onChange={(e) => setSessionDuration(e.target.value)}
              className="appearance-none w-full border border-gray-300  bg-white rounded-md px-4 py-2 text-sm  pr-10 focus:outline-none focus:ring-1 focus:ring-blue-500"
            >
              <option value="15 minutes">15 minutes</option>
              <option value="30 minutes">30 minutes</option>
              <option value="45 minutes">45 minutes</option>
              <option value="60 minutes">60 minutes</option>
            </select>
            <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center">
              <svg className="w-4 h-4 text-black" fill="none" stroke="currentColor" strokeWidth="3" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </div>
        </div>

        {/* Toggles */}
        <div className="space-y-4">
          {/* Toggle Row */}
          {[
            {
              label: "Auto-join patient at start",
              sub: "Automatically join video calls when session starts",
              value: autoJoin,
              set: setAutoJoin
            },
            {
              label: "Enable session notes popup",
              sub: "Show notes popup after each session",
              value: notesPopup,
              set: setNotesPopup
            },
            {
              label: "Require manual note approval",
              sub: "Review and approve notes before sending to patient",
              value: manualApproval,
              set: setManualApproval
            }
          ].map((item, i) => (
            <div key={i} className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-800">{item.label}</p>
                <p className="text-xs text-gray-500">{item.sub}</p>
              </div>
              <button
                onClick={() => item.set(!item.value)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 ${item.value ? 'bg-blue-500' : 'bg-gray-300'}`}
              >
                <span
                  className={`inline-block h-5 w-5 transform bg-white rounded-full transition-transform duration-200 ${item.value ? 'translate-x-5' : 'translate-x-1'}`}
                />
              </button>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-md p-6 shadow-sm">
        <h2 className="text-base font-semibold text-gray-900 mb-4">Notifications</h2>

        {/* Email Reminder Toggle */}
        <div className="flex items-center justify-between mb-4">
          <div>
            <p className="text-sm font-medium text-gray-800">Email reminders before session</p>
            <p className="text-xs text-gray-500">Send reminders 1 hour before session</p>
          </div>
          <button
            onClick={() => setEmailReminders(!emailReminders)}
            className={`relative inline-flex items-center h-6 w-11 rounded-full transition-colors duration-300 ${emailReminders ? 'bg-blue-500' : 'bg-gray-300'
              }`}
          >
            <span
              className={`inline-block h-5 w-5 transform bg-white rounded-full transition-transform duration-300 ${emailReminders ? 'translate-x-5' : 'translate-x-1'
                }`}
            />
          </button>
        </div>

        {/* Post-Session Summary Toggle */}
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-gray-800">Post-session summary emails</p>
            <p className="text-xs text-gray-500">Receive summary after each session</p>
          </div>
          <button
            onClick={() => setPostSessionEmails(!postSessionEmails)}
            className={`relative inline-flex items-center h-6 w-11 rounded-full transition-colors duration-300 ${postSessionEmails ? 'bg-blue-500' : 'bg-gray-300'
              }`}
          >
            <span
              className={`inline-block h-5 w-5 transform bg-white rounded-full transition-transform duration-300 ${postSessionEmails ? 'translate-x-5' : 'translate-x-1'
                }`}
            />
          </button>
        </div>
      </div>
      {/* Save Button */}
      <div className="flex flex-col gap-4 mt-6">
        {/* Message Display */}
        {saveMessage.text && (
          <div className={`p-3 rounded-md text-sm ${saveMessage.type === 'success'
              ? 'bg-green-50 text-green-700 border border-green-200'
              : 'bg-red-50 text-red-700 border border-red-200'
            }`}>
            {saveMessage.text}
          </div>
        )}

        <div className="flex items-center justify-between">
          <button className="text-sm text-gray-600 hover:underline">
            Reset to Default
          </button>

          <button
            onClick={saveAvailability}
            disabled={loadingAvailability}
            className={`text-sm font-semibold px-6 py-2 rounded-md transition
              bg-blue-600 text-white hover:bg-blue-700`}
          >
            {savingAvailability ? (
              <span className="flex items-center gap-2">
                <svg className="animate-spin h-4 w-4 text-white" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"/>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"/>
                </svg>
                Saving...
              </span>
            ) : 'Save All Changes'}
          </button>
        </div>
      </div>

      {/* Password Change Modal */}
      {showPasswordModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Change Password</h3>
              <button
                onClick={() => {
                  setShowPasswordModal(false);
                  setPasswordData({
                    currentPassword: '',
                    newPassword: '',
                    confirmPassword: ''
                  });
                  setPasswordMessage({ type: '', text: '' });
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Current Password
                </label>
                <input
                  type="password"
                  value={passwordData.currentPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Enter current password"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  New Password
                </label>
                <input
                  type="password"
                  value={passwordData.newPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Enter new password"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Confirm New Password
                </label>
                <input
                  type="password"
                  value={passwordData.confirmPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Confirm new password"
                />
              </div>

              {passwordMessage.text && (
                <div className={`p-3 rounded-md text-sm ${passwordMessage.type === 'success'
                    ? 'bg-green-50 text-green-700 border border-green-200'
                    : 'bg-red-50 text-red-700 border border-red-200'
                  }`}>
                  {passwordMessage.text}
                </div>
              )}

              <div className="flex justify-end gap-3 pt-4">
                <button
                  onClick={() => {
                    setShowPasswordModal(false);
                    setPasswordData({
                      currentPassword: '',
                      newPassword: '',
                      confirmPassword: ''
                    });
                    setPasswordMessage({ type: '', text: '' });
                  }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button
                  onClick={handlePasswordChange}
                  disabled={changingPassword}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {changingPassword ? 'Changing...' : 'Change Password'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default Settings;
